/**
 * Register/Login/Forgot Password Page
 * ZO Bull Algo Trading Platform
 *
 * Version: 6.2.0
 * 
 * Changes in v6.2.0:
 * - Added support for ?mode=forgot query parameter to pre-select Forgot Password tab
 * - Improved user flow when navigating from Login page's Forgot Password link
 */
import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import apiClient from '../api/apiClient'
import './Login.css'

type AuthMode = 'login' | 'register' | 'forgot'

interface LoginResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  user_id: string
  username: string
  role: string
}

interface RegisterResponse {
  id: string
  username: string
  email: string
  role: string
  is_active: boolean
}

const Register: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [mode, setMode] = useState<AuthMode>('login')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [regName, setRegName] = useState('')
  const [regEmail, setRegEmail] = useState('')
  const [regPassword, setRegPassword] = useState('')
  const [regConfirmPassword, setRegConfirmPassword] = useState('')
  const [regPhone, setRegPhone] = useState('')
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')

  // Check for mode query parameter to pre-select Forgot Password tab
  useEffect(() => {
    const modeParam = searchParams.get('mode')
    if (modeParam === 'forgot') {
      setMode('forgot')
      setError('')
      setSuccess('')
    }
  }, [searchParams])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    if (!loginEmail || !loginPassword) {
      setError('Please enter email and password')
      setLoading(false)
      return
    }
    try {
      const response = await apiClient.post<LoginResponse>('/api/auth/login', {
        username: loginEmail,
        password: loginPassword,
      })
      localStorage.setItem('auth_token', response.data.access_token)
      localStorage.setItem('refresh_token', response.data.refresh_token)
      localStorage.setItem('user_id', response.data.user_id)
      localStorage.setItem('username', response.data.username)
      localStorage.setItem('user_role', response.data.role)
      navigate('/')
    } catch (err: any) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail)
      } else {
        setError('Connection error. Please check if the server is running.')
      }
      setLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    if (!regName || !regEmail || !regPassword) {
      setError('Please fill in all required fields')
      setLoading(false)
      return
    }
    if (regPassword !== regConfirmPassword) {
      setError('Passwords do not match')
      setLoading(false)
      return
    }
    if (regPassword.length < 8) {
      setError('Password must be at least 8 characters')
      setLoading(false)
      return
    }
    if (!acceptTerms) {
      setError('Please accept the Terms of Service')
      setLoading(false)
      return
    }
    try {
      await apiClient.post<RegisterResponse>('/api/auth/register', {
        username: regName,
        email: regEmail,
        password: regPassword,
      })
      setSuccess('Registration successful! You can now login.')
      setMode('login')
      setLoginEmail(regEmail)
      setLoginPassword('')
      setRegName('')
      setRegEmail('')
      setRegPassword('')
      setRegConfirmPassword('')
    } catch (err: any) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail)
      } else {
        setError('Registration failed. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    if (!forgotEmail) {
      setError('Please enter your email address')
      setLoading(false)
      return
    }
    setSuccess('Password reset link sent! Please check your email.')
    setLoading(false)
    setMode('login')
  }

  return (
    <div className="login-page">
      <div className="login-container register-container">
        <div className="login-header">
          <div className="logo-section">
            <span className="logo-icon">🐂</span>
            <h1>ZO Bull</h1>
          </div>
          <p className="tagline">ZO Bull Algo Trading Platform</p>
        </div>
        <div className="auth-tabs">
          <button className={`auth-tab ${mode === 'login' ? 'active' : ''}`} onClick={() => { setMode('login'); setError(''); setSuccess(''); }}>Login</button>
          <button className={`auth-tab ${mode === 'register' ? 'active' : ''}`} onClick={() => { setMode('register'); setError(''); setSuccess(''); }}>Register</button>
          <button className={`auth-tab ${mode === 'forgot' ? 'active' : ''}`} onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}>Forgot Password</button>
        </div>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        {mode === 'login' && (
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="login-email">Email</label>
              <input type="email" id="login-email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="your@email.com" disabled={loading} />
            </div>
            <div className="form-group">
              <label htmlFor="login-password">Password <button type="button" className="forgot-link" onClick={() => setMode('forgot')}>Forgot?</button></label>
              <input type="password" id="login-password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="Enter password" disabled={loading} />
            </div>
            <button type="submit" className="btn btn-primary login-btn" disabled={loading}>{loading ? 'Logging in...' : 'Login'}</button>
            <div className="form-divider"><span>or</span></div>
            <p className="switch-mode">Don't have an account? <button type="button" onClick={() => setMode('register')}>Sign Up Free</button></p>
          </form>
        )}
        {mode === 'register' && (
          <form onSubmit={handleRegister} className="login-form">
            <div className="form-group"><label htmlFor="reg-name">Full Name *</label><input type="text" id="reg-name" value={regName} onChange={(e) => setRegName(e.target.value)} placeholder="John Doe" disabled={loading} /></div>
            <div className="form-group"><label htmlFor="reg-email">Email *</label><input type="email" id="reg-email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} placeholder="your@email.com" disabled={loading} /></div>
            <div className="form-group"><label htmlFor="reg-phone">Phone (Optional)</label><input type="tel" id="reg-phone" value={regPhone} onChange={(e) => setRegPhone(e.target.value)} placeholder="+91 98765 43210" disabled={loading} /></div>
            <div className="form-group"><label htmlFor="reg-password">Password *</label><input type="password" id="reg-password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} placeholder="Min 8 characters" disabled={loading} /></div>
            <div className="form-group"><label htmlFor="reg-confirm">Confirm Password *</label><input type="password" id="reg-confirm" value={regConfirmPassword} onChange={(e) => setRegConfirmPassword(e.target.value)} placeholder="Confirm password" disabled={loading} /></div>
            <div className="form-group checkbox-group"><label className="checkbox-label"><input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} disabled={loading} /><span>I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link></span></label></div>
            <button type="submit" className="btn btn-primary login-btn" disabled={loading}>{loading ? 'Creating Account...' : 'Create Account'}</button>
            <div className="form-divider"><span>or</span></div>
            <p className="switch-mode">Already have an account? <button type="button" onClick={() => setMode('login')}>Login</button></p>
          </form>
        )}
        {mode === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="login-form">
            <div className="forgot-info"><p>Enter your email address and we'll send you a link to reset your password.</p></div>
            <div className="form-group"><label htmlFor="forgot-email">Email</label><input type="email" id="forgot-email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="your@email.com" disabled={loading} /></div>
            <button type="submit" className="btn btn-primary login-btn" disabled={loading}>{loading ? 'Sending...' : 'Send Reset Link'}</button>
            <div className="form-divider"><span>or</span></div>
            <p className="switch-mode">Remember your password? <button type="button" onClick={() => setMode('login')}>Login</button></p>
          </form>
        )}
        <div className="login-footer">
          <div className="footer-links"><Link to="/landing">← Back to Home</Link><span className="dot">•</span><Link to="/terms">Terms</Link><span className="dot">•</span><Link to="/privacy">Privacy</Link></div>
          <p>ZO Bull v6.2.0 | © 2024-2026</p>
        </div>
      </div>
    </div>
  )
}

export default Register
